<template>
    <div class="event-management">
        <DevelopmentWarning />

        <div class="header-section">
            <div class="header-left">
                <h2>Speaker Roster</h2>
                <div class="filter-section">
                    <select v-model="selectedTimeFilter" class="filter-dropdown">
                        <option value="upcoming">Upcoming Events</option>
                        <option value="all">All Events</option>
                        <option value="past">Past Events</option>
                    </select>
                    <select v-model="selectedFormatFilter" class="filter-dropdown">
                        <option value="">All Formats</option>
                        <option v-for="format in formats" :key="format.id" :value="format.id">
                            {{ format.name }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Speaker Roster Table -->
        <div class="events-table">
            <table>
                <thead>
                    <tr>
                        <th @click="sortBy('event_date')" class="sortable text-left">
                            <span class="desktop-text">Event Date</span>
                            <span class="mobile-text">Date</span>
                            <span class="sort-icon" v-if="sortField === 'event_date'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('meeting_format_name')" class="sortable text-left">
                            <span class="desktop-text">Meeting Format</span>
                            <span class="mobile-text">Format</span>
                            <span class="sort-icon" v-if="sortField === 'meeting_format_name'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('first_speaker.name')" class="sortable text-left">
                            <span class="desktop-text">First Speaker</span>
                            <span class="mobile-text">Speaker 1</span>
                            <span class="sort-icon" v-if="sortField === 'first_speaker.name'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th @click="sortBy('second_speaker.name')" class="sortable text-left">
                            <span class="desktop-text">Second Speaker</span>
                            <span class="mobile-text">Speaker 2</span>
                            <span class="sort-icon" v-if="sortField === 'second_speaker.name'">
                                {{ sortDirection === 'asc' ? '↑' : '↓' }}
                            </span>
                        </th>
                        <th class="text-left" style="width: 100px;">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td colspan="5" style="text-align: center; padding: 2rem; color: #6c757d;">
                            Loading speaker roster...
                        </td>
                    </tr>
                    <tr v-else-if="error">
                        <td colspan="5" style="text-align: center; padding: 2rem; color: #dc3545;">
                            Error loading data: {{ error }}
                        </td>
                    </tr>
                    <tr v-else-if="filteredRoster.length === 0">
                        <td colspan="5" style="text-align: center; padding: 2rem; color: #6c757d;">
                            No events found for the selected filters.
                        </td>
                    </tr>
                    <tr v-else v-for="event in filteredRoster" 
                        :key="event.event_id"
                        :class="{ 'past-event': getEventStatus(event.event_date) === 'past' }"
                    >
                        <td class="text-left">
                            <div class="date-display">
                                <div class="weekday">{{ new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short' }) }}</div>
                                <div class="date">{{ new Date(event.event_date).getDate() }} {{ new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' }) }} {{ new Date(event.event_date).getFullYear().toString().substr(2) }}</div>
                                <div class="time">{{ formatTime(event.event_date) }}</div>
                            </div>
                        </td>
                        <td class="text-left">
                            <span class="format-name">{{ event.meeting_format_name }}</span>
                        </td>
                        <td class="text-left">
                            <div class="speaker-info">
                                <div class="speaker-name">{{ event.first_speaker.name }}</div>
                                <div v-if="event.first_speaker.topic" class="speaker-topic">{{ event.first_speaker.topic }}</div>
                                <div class="speaker-last-spoke">{{ getSpeakerLastSpoke(event.first_speaker) }}</div>
                            </div>
                        </td>
                        <td class="text-left">
                            <div class="speaker-info">
                                <div class="speaker-name">{{ event.second_speaker.name }}</div>
                                <div v-if="event.second_speaker.topic" class="speaker-topic">{{ event.second_speaker.topic }}</div>
                                <div class="speaker-last-spoke">{{ getSpeakerLastSpoke(event.second_speaker) }}</div>
                            </div>
                        </td>
                        <td class="actions-cell">
                            <div class="dropdown" :class="{ active: activeDropdown === event.event_id }">
                                <button class="btn-icon" @click.stop="toggleDropdown(event)">
                                    <i class="material-icons">more_vert</i>
                                </button>
                                <div class="dropdown-menu" v-if="activeDropdown === event.event_id">
                                    <button class="dropdown-item" @click="viewEventDetails(event)">
                                        <i class="material-icons">visibility</i>
                                        <span>View</span>
                                    </button>
                                    <button class="dropdown-item" @click="editEvent(event)">
                                        <i class="material-icons">edit</i>
                                        <span>Edit</span>
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Event Details Modal (View) -->
        <EventDetailsModal
            v-if="showDetailsModal"
            :event="selectedEvent"
            :current-user="currentUser"
            :formats="formats"
            :is-editing="false"
            @close="closeDetailsModal"
            @session-updated="loadSpeakerRoster"
        />

        <!-- Event Modal (Add/Edit) -->
        <EventModal
            v-if="showAddEventModal"
            :is-editing="!!editingEvent"
            :editing-event="editingEvent"
            :venues="venues"
            :members="members"
            :formats="formats"
            @save="saveEvent"
            @close="closeEventModal"
            @update:members="members = $event"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { eventService } from '../services/eventService';
import DevelopmentWarning from './common/DevelopmentWarning.vue';
import EventDetailsModal from './EventDetailsModal.vue';
import EventModal from './EventModal.vue';

export default {
    name: 'SpeakerRoster',
    components: {
        DevelopmentWarning,
        EventDetailsModal,
        EventModal
    },
    setup() {
        // ==========================================
        // State Management
        // ==========================================
        
        const speakerRoster = ref([]);
        const formats = ref([]);
        const loading = ref(true);
        const error = ref('');
        const speakingHistory = ref({});
        const activeDropdown = ref(null);
        const showDetailsModal = ref(false);
        const showAddEventModal = ref(false);
        const selectedEvent = ref(null);
        const editingEvent = ref(null);
        const venues = ref([]);
        const members = ref([]);
        const currentUser = ref(null);
        
        // Filter states
        const selectedTimeFilter = ref('upcoming');
        const selectedFormatFilter = ref('');
        
        // Sorting states
        const sortField = ref('event_date');
        const sortDirection = ref('asc');

        // ==========================================
        // Data Loading
        // ==========================================
        
        const loadSpeakerRoster = async () => {
            try {
                loading.value = true;
                error.value = '';
                
                const roster = await eventService.getSpeakerRoster();
                
                if (roster && roster.data) {
                    speakerRoster.value = roster.data;
                } else if (Array.isArray(roster)) {
                    speakerRoster.value = roster;
                } else {
                    speakerRoster.value = [];
                    error.value = 'Unexpected data format received from server';
                }
            } catch (err) {
                console.error('Error loading speaker roster:', err);
                speakerRoster.value = [];
                error.value = err.message || 'Failed to load speaker roster';
            } finally {
                loading.value = false;
            }
        };

        const loadFormats = async () => {
            try {
                const formatsResponse = await eventService.getMeetingFormats();
                formats.value = formatsResponse || [];
            } catch (err) {
                console.error('Error loading formats:', err);
                formats.value = [];
            }
        };

        const loadSpeakingHistory = async () => {
            try {
                const history = await eventService.getMemberSpeakingHistory();
                speakingHistory.value = history;
            } catch (err) {
                console.error('Error loading speaking history:', err);
                speakingHistory.value = {};
            }
        };

        const loadVenues = async () => {
            try {
                const venuesResponse = await eventService.getAllVenues();
                venues.value = venuesResponse || [];
            } catch (err) {
                console.error('Error loading venues:', err);
                venues.value = [];
            }
        };

        const loadMembers = async () => {
            try {
                const membersResponse = await eventService.getAllMembers();
                members.value = membersResponse || [];
            } catch (err) {
                console.error('Error loading members:', err);
                members.value = [];
            }
        };

        // ==========================================
        // Computed Properties
        // ==========================================
        
        const filteredRoster = computed(() => {
            let filtered = [...speakerRoster.value];
            
            // Time filter
            if (selectedTimeFilter.value !== 'all') {
                const now = new Date();
                filtered = filtered.filter(event => {
                    const eventDate = new Date(event.event_date);
                    if (selectedTimeFilter.value === 'upcoming') {
                        return eventDate >= now;
                    } else if (selectedTimeFilter.value === 'past') {
                        return eventDate < now;
                    }
                    return true;
                });
            }
            
            // Format filter
            if (selectedFormatFilter.value) {
                const formatName = formats.value.find(f => f.id == selectedFormatFilter.value)?.name;
                filtered = filtered.filter(event => event.meeting_format_name === formatName);
            }
            
            // Sort
            filtered.sort((a, b) => {
                let aValue, bValue;
                
                if (sortField.value === 'first_speaker.name') {
                    aValue = a.first_speaker.name;
                    bValue = b.first_speaker.name;
                } else if (sortField.value === 'second_speaker.name') {
                    aValue = a.second_speaker.name;
                    bValue = b.second_speaker.name;
                } else {
                    aValue = a[sortField.value];
                    bValue = b[sortField.value];
                }
                
                if (aValue < bValue) return sortDirection.value === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection.value === 'asc' ? 1 : -1;
                return 0;
            });
            
            return filtered;
        });

        // ==========================================
        // Helper Functions
        // ==========================================
        
        const getEventStatus = (eventDate) => {
            const now = new Date();
            const event = new Date(eventDate);
            return event < now ? 'past' : 'upcoming';
        };

        const formatTime = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        };

        const getSpeakerLastSpoke = (speaker) => {
            if (speaker.name === 'TBD' || !speaker.speaker_id) {
                return '';
            }
            
            const history = speakingHistory.value[speaker.speaker_id];
            if (!history || !history.last_spoke) {
                return 'Never spoke';
            }
            
            const lastSpoke = new Date(history.last_spoke);
            const today = new Date();
            const diffTime = Math.abs(today - lastSpoke);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays === 1) {
                return '1 day ago';
            } else {
                return `${diffDays} days ago`;
            }
        };

        const toggleDropdown = (event) => {
            activeDropdown.value = activeDropdown.value === event.event_id ? null : event.event_id;
        };

        const viewEventDetails = async (event) => {
            try {
                const eventDetails = await eventService.getEventDetails(event.event_id);
                selectedEvent.value = eventDetails;
                showDetailsModal.value = true;
                activeDropdown.value = null;
            } catch (error) {
                console.error('Error fetching event details:', error);
            }
        };

        const editEvent = async (event) => {
            try {
                const eventDetails = await eventService.getEventDetails(event.event_id);

                const eventToEdit = {
                    id: eventDetails.id,
                    event_type: eventDetails.event_type || 'Regular Meeting',
                    format_id: eventDetails.format_id,
                    event_date: eventDetails.event_date,
                    end_time: eventDetails.end_time,
                    venue_id: eventDetails.venue_id,
                    intro_topic: eventDetails.intro_topic || '',
                    sessions: eventDetails.sessions || []
                };

                editingEvent.value = eventToEdit;
                showAddEventModal.value = true;
                activeDropdown.value = null;
            } catch (error) {
                console.error('Error editing event:', error);
            }
        };

        const closeDetailsModal = () => {
            showDetailsModal.value = false;
            selectedEvent.value = null;
        };

        const closeEventModal = () => {
            showAddEventModal.value = false;
            editingEvent.value = null;
        };

        const saveEvent = async (eventData) => {
            try {
                if (eventData.isEditing) {
                    await eventService.updateEvent(eventData.editingEventId, eventData.data);
                } else {
                    await eventService.createEvent(eventData.data);
                }
                await loadSpeakerRoster(); // Reload the speaker roster
                closeEventModal();
            } catch (error) {
                console.error('Error saving event:', error);
            }
        };

        const sortBy = (field) => {
            if (sortField.value === field) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortField.value = field;
                sortDirection.value = 'asc';
            }
        };

        // ==========================================
        // Lifecycle
        // ==========================================
        
        onMounted(async () => {
            await Promise.all([
                loadSpeakerRoster(),
                loadFormats(),
                loadSpeakingHistory(),
                loadVenues(),
                loadMembers()
            ]);
        });

        return {
            speakerRoster,
            formats,
            loading,
            error,
            selectedTimeFilter,
            selectedFormatFilter,
            sortField,
            sortDirection,
            filteredRoster,
            getEventStatus,
            formatTime,
            getSpeakerLastSpoke,
            sortBy,
            activeDropdown,
            toggleDropdown,
            viewEventDetails,
            editEvent,
            showDetailsModal,
            showAddEventModal,
            selectedEvent,
            editingEvent,
            venues,
            members,
            currentUser,
            closeDetailsModal,
            closeEventModal,
            saveEvent
        };
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

/* Copy exact styles from EventManagement.vue */
.event-management {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.header-left h2 {
    margin: 0;
    color: #2c3e50;
    font-size: 1.8rem;
}

.filter-section {
    display: flex;
    gap: 1rem;
}

.filter-dropdown {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
    min-width: 150px;
    font-size: 0.9rem;
}

.events-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: visible;
    position: relative;
    z-index: 1;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th {
    text-align: left;
    padding: 1rem;
    background: #f8f9fa;
    font-weight: 500;
    color: #2c3e50;
    border-bottom: 2px solid #e9ecef;
    position: sticky;
    top: 0;
    z-index: 10;
}

td {
    padding: 1rem;
    border-bottom: 1px solid #e9ecef;
    vertical-align: top;
}

.sortable {
    cursor: pointer;
    user-select: none;
}

.sortable:hover {
    background-color: #f0f4f8;
}

.sort-icon {
    margin-left: 0.5rem;
    font-size: 0.8rem;
}

.text-left {
    text-align: left;
}

.date-display {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.weekday {
    font-size: 0.8rem;
    color: #6c757d;
    text-transform: uppercase;
    font-weight: 500;
}

.date {
    font-weight: 600;
    color: #2c3e50;
}

.time {
    font-size: 0.85rem;
    color: #6c757d;
}

.format-name {
    font-weight: 500;
    color: #2c3e50;
}

.speaker-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.speaker-name {
    font-weight: 600;
    color: #2c3e50;
}

.speaker-topic {
    font-size: 0.85rem;
    color: #6c757d;
    font-style: italic;
}

.speaker-last-spoke {
    font-size: 0.75rem;
    color: #6c757d;
    font-weight: 500;
}

.past-event {
    opacity: 0.6;
}

/* Actions dropdown styles */
.actions-cell {
    position: relative;
    text-align: center;
    width: 100px;
}

.dropdown {
    position: relative;
    display: inline-block;
}

.btn-icon {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;
}

.btn-icon:hover {
    background-color: #f0f4f8;
}

.material-icons {
    font-size: 20px;
    color: #6c757d;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    min-width: 140px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    z-index: 1000;
    border: 1px solid #e9ecef;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 0.875rem;
}

.dropdown-item:hover {
    background-color: #f8f9fa;
}

.dropdown-item:first-child {
    border-radius: 4px 4px 0 0;
}

.dropdown-item:last-child {
    border-radius: 0 0 4px 4px;
}

.dropdown-item .material-icons {
    font-size: 16px;
}

/* Mobile responsive design matching EventManagement exactly */
@media (max-width: 768px) {
    .event-management {
        padding: 1rem;
    }
    
    .header-section {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }
    
    .header-left {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }
    
    .filter-section {
        flex-direction: column;
        gap: 0.5rem;
    }
    
    .events-table {
        overflow-x: auto;
    }
    
    table {
        min-width: 600px;
    }
    
    th,
    td {
        padding: 0.75rem 0.5rem;
    }
    
    .desktop-text {
        display: none;
    }
    
    .mobile-text {
        display: inline;
    }
}

@media (min-width: 769px) {
    .mobile-text {
        display: none;
    }
    
    .desktop-text {
        display: inline;
    }
}
</style>